import useSlideOver from '../../../useSlideOver';
import { NARROW_SLIDE_OVER_WIDTH, useSlideOverWidth } from '../../../wideSlideOver';
import UseContext from '../../../../../hooks/useContext';
import SlideOverHeader from '../../../component/header';
import SlideOverBody from '../../../component/body';
import SlideOverFooter from '../../../component/footer';
import ApiDatabase from 'server';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  RotateFadingClock,
} from '../../../../../assets/icons';
import Table from '../../../../table/table';
import Banner from '../../../../banner/banner';
import Button from '../../../../button';
import CopyToClipboard from '../../../../copyToClipboard';
import validateCoefficientRanges from 'helper/validateCoefficientRanges';
import { isDefaultCoefficientRanges } from 'helper/coefficientRange';
import { formatLastUpdate, formatRelativeDay } from 'helper/lastUpdate';
import SlideOverCompanyOverview from './slideOverCompanyOverview';
import SlideOverCompanyContactsTab from './SlideOverCompanyContactsTab';
import SlideOverCompanyRemunerationConditions from './slideOverCompanyRemunerationConditions';
import useRemunerationDraft from 'hooks/useRemunerationDraft';
import SlideOverCompanyInvoicesTab from './SlideOverCompanyInvoicesTab';
import SlideOverCompanyMissions from './slideOverCompanyMissions';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import CompanyContactEditView, { EDIT_SCOPES } from './CompanyContactEditView';
import useCompanyPerimeters from './useCompanyPerimeters';
import useTaskPanels from 'hooks/useTaskPanels';

// The panel shows the contacts and the missions; the rest of a company is on
// its page (« Vue détaillée »). A screen that opens the panel on another tab
// (`target`: the remuneration conditions of a timesheet, the invoices of the
// billing, the overview the company page edits) gets that tab beside them.
const PANEL_TABS = ['members', 'missions'];
const TARGET_TABS = ['overview', 'remuneration_conditions', 'invoices', 'members', 'missions'];
// The company page opens the « Paramètres de paiement » screen straight away,
// over the remuneration conditions tab.
const COEFFICIENTS_DRAFT_KEY = 'coefficients';
export const PAYMENT_SETTINGS_TARGET = 'payment_settings';
// A contact page opens the panel on one member's edit, on what it edits there
// (`memberTarget(id, scope)`, EDIT_SCOPES): its « back » closes the panel.
const MEMBER_TARGET_PREFIX = 'member:';
export const memberTarget = (memberId, scope = EDIT_SCOPES.all) => `${MEMBER_TARGET_PREFIX}${scope}:${memberId}`;
const memberTargetOf = (target) => {
  if (typeof target !== 'string' || !target.startsWith(MEMBER_TARGET_PREFIX)) return null;
  const [scope, memberId] = target.slice(MEMBER_TARGET_PREFIX.length).split(':');
  return memberId ? { scope, memberId } : null;
};
const memberOfTarget = (target) => memberTargetOf(target)?.memberId ?? null;
// What the opening screen already knows of the member (`seedMember`): the
// edit shows it at once, before the company is read.
const memberSeeds = new Map();
export const seedMember = (companyId, member) => memberSeeds.set(`${companyId}:${member.user._id}`, member);
const pageOfTarget = (target) => {
  if (target === PAYMENT_SETTINGS_TARGET) return 'remuneration_conditions';
  return TARGET_TABS.includes(target) ? target : 'members';
};

const SlideOverCompany = () => {
  const { removeAllParameters } = UseContext();
  const {
    objID,
    target,
    setTarget,
    userData,
    modalDataReturn,
    setModalDataReturn,
    refreshCompanyBigAccount,
    language,
    companyBackTaskId,
  } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const { openTask } = useTaskPanels();
  const notifyRecordChanged = useNotifyRecordChanged();
  const [companyInfo, setCompanyInfo] = useState({});
  const token = localStorage.getItem('xsrfToken');
  const [isShowCoeff, setIsShowCoeff] = useState(target === PAYMENT_SETTINGS_TARGET);
  // The contact being edited: its screen replaces the panel, like the coefficients one.
  const [editedMemberId, setEditedMemberId] = useState(null);
  // The payment settings and a contact's edit read as a task form: same 600 px panel.
  useSlideOverWidth(NARROW_SLIDE_OVER_WIDTH, isShowCoeff || Boolean(editedMemberId) || Boolean(memberOfTarget(target)));
  const [isWaiting, setIsWaiting] = useState(false);
  // The sector and the big account are fetched from here, beside the company,
  // so the three requests leave together — under the overview they could only
  // start once the panel had replaced its skeleton.
  const [pennylaneCategoryList, setPennylaneCategoryList] = useState([]);
  const [bigAccount, setBigAccount] = useState('');
  const [isCategoriesWaiting, setIsCategoriesWaiting] = useState(true);
  const [isBigAccountWaiting, setIsBigAccountWaiting] = useState(true);
  const [pageType, setPageType] = useState(() => pageOfTarget(target));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Read here rather than in the contacts tab: the contact screen replaces
  // the tab and shows the member's perimeters from the same lists. Only read
  // while the tab (or its contact screen) is on.
  // A contact page's opening on a member reads them at once, before the company.
  const perimeters = useCompanyPerimeters(
    pageType === 'members' ? companyInfo?._id ?? (memberOfTarget(target) ? objID : undefined) : undefined
  );
  const [isCoeffSend, setIsCoeffSend] = useState(false);
  // The write is fired from the confirmation modal, not from the button below,
  // so without this the panel gave no sign anything was happening — and a
  // second click sent the ranges twice.
  const scrollContainerRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const titlesCoeff = [
    'TRANCHES DE SALAIRE BRUT MENSUEL',
    'COEFF. DÉLÉGATION',
    'COEFF. GESTION',
  ];

  // Local state, lifted here instead of the shared context: adding and
  // removing rows does not work well through state shared with other pages.
  const [coeffRows, setCoeffRows] = useState([]);
  const [coeffErrors, setCoeffErrors] = useState([]);
  const [coeffBanner, setCoeffBanner] = useState(null);
  // Only validate live once something has been edited: on open the panel
  // reports what is stored, it does not accuse the user of a mistake they
  // have not made yet.
  const [coeffTouched, setCoeffTouched] = useState(false);
  // The attestation lives in the confirmation modal since JBWEB-1214, beside
  // the document it now attests to. It reaches the save from there.

  // The grid joins the page's other remuneration changes, saved together under
  // one justificatif (JBWEB-1468); reopened, the editor shows the one held back.
  const { draft: remunerationDraft } = useRemunerationDraft(objID);

  useEffect(() => {
    const staged = remunerationDraft.pending(COEFFICIENTS_DRAFT_KEY);
    setCoeffRows(staged ?? companyInfo?.coefficients ?? []);
    setCoeffErrors([]);
    setCoeffBanner(null);
    setCoeffTouched(Boolean(staged));
  }, [companyInfo?.coefficients]);

  // Live validation, so the banner does not wait for Confirmer. One debounce
  // covers both cases: leaving a field commits its value to coeffRows, and
  // typing in one commits on each keystroke — either way the check runs 300 ms
  // after the last change. It also clears the red as soon as a field becomes
  // valid again, which JBWEB-1074 asks for.
  const markCoeffTouched = (updater) => {
    setCoeffTouched(true);
    setCoeffRows(updater);
  };

  // Validated on open, not only after an edit: the SMIC rises while stored
  // grids stay put, so a company can fall under it without anyone touching the
  // panel. The server refuses those same grids on save, so showing the alert
  // here only makes an existing refusal visible earlier.
  useEffect(() => {
    const timer = setTimeout(() => {
      const errors = validateCoefficientRanges(coeffRows);
      setCoeffErrors(errors);
      setCoeffBanner(errors.length > 0 ? errors[0].message : null);
    }, 300);
    return () => clearTimeout(timer);
  }, [coeffRows]);

  useEffect(() => {
    setPageType(pageOfTarget(target));
    setIsShowCoeff(target === PAYMENT_SETTINGS_TARGET);
    setEditedMemberId(memberOfTarget(target));
  }, [target, objID]);

  const fetchCompanyInfo = (showLoader = true) => {
    if (!objID) return;
    if (showLoader) setIsWaiting(true);
    ApiDatabase.getCompanyInfo(
      { token, idCompany: objID },
      (data) => {
        setCompanyInfo(data.company);
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaiting(false);
        }
      }
    );
  };

  useEffect(() => {
    // Another company: no member edited, unless the opening asks for one.
    setEditedMemberId(memberOfTarget(target));
    fetchCompanyInfo();
  }, [objID]);

  const loadPennylaneCategories = () => {
    setIsCategoriesWaiting(true);
    ApiDatabase.getPennyLaneCategories(
      { token },
      (data) => {
        setPennylaneCategoryList(data?.items || []);
        setIsCategoriesWaiting(false);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.error(err);
        setIsCategoriesWaiting(false);
      }
    );
  };

  useEffect(() => {
    loadPennylaneCategories();
  }, []);

  useEffect(() => {
    if (!objID) return;
    setIsBigAccountWaiting(true);
    ApiDatabase.getCompanyBigAccount(
      { token, idCompany: objID },
      (data) => {
        setBigAccount(data?.bigAccount ?? '');
        setIsBigAccountWaiting(false);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.error(err);
        setIsBigAccountWaiting(false);
      }
    );
  }, [objID, refreshCompanyBigAccount]);

  useEffect(() => {
    if (!modalDataReturn) return;
    if (modalDataReturn?.action === 'invoices_deleted' && modalDataReturn.invoice_numbers) {
      setCompanyInfo((prev) => {
        if (!prev?.factures) return prev;
        return {
          ...prev,
          factures: prev.factures.filter(
            (f) => !modalDataReturn.invoice_numbers.includes(f.external_id)
          ),
        };
      });
    } else if (modalDataReturn?.action === 'invoices_imported' && modalDataReturn.invoice_numbers) {
      setCompanyInfo((prev) => {
        if (!prev?.factures) return prev;
        return {
          ...prev,
          factures: prev.factures.map((f) =>
            modalDataReturn.invoice_numbers.includes(f.external_id)
              ? { ...f, payment_status: f.payment_status || 'pending' }
              : f
          ),
        };
      });
    }
  }, [modalDataReturn]);

  const closeSlideOver = () => {
    toggleSlideOver();
    removeAllParameters();
    // The next opening shows the panel's own tabs unless it asks for another.
    setTarget?.('');
  };
  const companyPath = `/company/${companyInfo?._id}`;
  const openDetailedView = () => {
    toggleSlideOver();
    setTarget?.('');
    navigate(companyPath);
  };
  const shownTabs = PANEL_TABS.includes(pageOfTarget(target))
    ? PANEL_TABS
    : [...PANEL_TABS, pageOfTarget(target)];
  const tabLabels = {
    overview: "Vue d'ensemble",
    members: language.getValue('companyContacts.tabLabel', 'Contacts'),
    remuneration_conditions: 'Conditions de rémunération',
    missions: 'Missions',
    invoices: 'Factures',
  };

  const checkScrollable = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setIsScrollable(scrollLeft > 0 || scrollLeft + clientWidth < scrollWidth);
    }
  };

  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, []);

  const handleScroll = () => checkScrollable();

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // The grid joins the page's changes once it validates; nothing is persisted
  // until the batch is confirmed, with its justificatif. The back replays the
  // same rules on save (values end up as billing rates and the front can be
  // bypassed), so this front-side copy is a UX shortcut, not the source of
  // truth. It needs something to write and a grid without alerts.
  const canSubmitCoeff = coeffTouched && coeffErrors.length === 0;

  const submitCoeff = () => {
    if (!canSubmitCoeff) return;
    const errors = validateCoefficientRanges(coeffRows);
    if (errors.length > 0) {
      setCoeffErrors(errors);
      setCoeffBanner(errors[0].message);
      return;
    }
    setCoeffErrors([]);
    setCoeffBanner(null);
    const coefficients = coeffRows;
    if (JSON.stringify(coefficients) === JSON.stringify(companyInfo?.coefficients ?? [])) {
      remunerationDraft.remove(COEFFICIENTS_DRAFT_KEY);
    } else {
      remunerationDraft.stage({
        key: COEFFICIENTS_DRAFT_KEY,
        section: 'coefficients',
        label: 'Coefficients de facturation',
        before: null,
        after: `Grille modifiée · ${coefficients.length} tranche${coefficients.length > 1 ? 's' : ''}`,
        form: coefficients,
        preview: { type: 'coefficients_updated', coefficients },
        // The batch's justificatif is the attestation the api asks for: it
        // cannot be confirmed without the box ticked.
        commit: (justificatif) =>
          new Promise((resolve, reject) =>
            ApiDatabase.postCoefficient(
              { token, idCompany: objID, coefficients, attested: true, justificatif },
              () => resolve({ type: 'coefficients_updated', coefficients }),
              reject,
            ),
          ),
      });
    }
    setCoeffTouched(false);
    if (target === PAYMENT_SETTINGS_TARGET) closeSlideOver();
    else setIsShowCoeff(false);
  };

  useEffect(() => {
    // The Pennylane sector decides which default coefficient grid applies, and
    // the panel names that sector on two of its labels. Patching the id in
    // place would leave both stale, so the company is read back instead.
    if (modalDataReturn?.type === 'company_activity_updated') {
      fetchCompanyInfo(false);
      setModalDataReturn(null);
    }
  }, [modalDataReturn]);

  const restoreDefaults = () => {
    setCoeffTouched(true);
    setCoeffRows(
      (companyInfo?.defaultCoefficients ?? []).map((range) => ({ ...range }))
    );
    setCoeffErrors([]);
    setCoeffBanner(null);
  };

  // Leaving the panel unmounts it, but coeffRows lives here: without an
  // explicit reset, reopening would show the abandoned draft as if it had
  // been stored. Opened straight on this screen, cancelling closes the panel.
  const discardCoeffDraft = () => {
    setCoeffRows((companyInfo?.coefficients ?? []).map((range) => ({ ...range })));
    setCoeffErrors([]);
    setCoeffBanner(null);
    setCoeffTouched(false);
    if (target === PAYMENT_SETTINGS_TARGET) closeSlideOver();
    else setIsShowCoeff(false);
  };

  // The stored ranges still matching the template is what "default values"
  // means here — nothing records it. Kept as an additional reason to warn, on
  // top of the incomplete case isCoeffSend already covers.
  const isCoeffOnDefaults = isDefaultCoefficientRanges(
    companyInfo?.coefficients,
    companyInfo?.defaultCoefficients
  );

  // The API resolves the sector from the company's PennyLane category, and
  // leaves it null when the category maps to no grid. Naming it tells the
  // admin which grid the button is about to apply.
  const defaultCoefficientsSector = companyInfo?.defaultCoefficientsSector;

  // Nothing to show until the ranges have been saved once: coefficients cannot
  // be written without attesting, so a stored set is always an attested one.
  const coefficientsAttestation = companyInfo?.coefficientsAttestation;
  const attestationLabel = coefficientsAttestation?.at
    ? `Attesté ${formatRelativeDay(coefficientsAttestation.at)}${
        coefficientsAttestation.by
          ? ` par ${coefficientsAttestation.by.firstname} ${(
              coefficientsAttestation.by.lastname ?? ''
            ).toUpperCase()}`
          : ''
      }`
    : null;

  // Who last touched the grid, beside who attested it: the attestation says the
  // values were vouched for, this says when they last moved. Same two events the
  // Coefficient de facturation card reads on the page behind.
  const coefficientsLastUpdate = formatLastUpdate(
    companyInfo?.lastUpdates?.modification_coefficient_facturation ??
      companyInfo?.lastUpdates?.ajout_coefficient_facturation
  );

  // The company read is this panel's (`objID`): one still showing the previous
  // company is not used.
  const shownCompany = companyInfo?._id === objID ? companyInfo : null;
  const editedMember =
    (shownCompany?.employees ?? []).find((entry) => entry.user?._id === editedMemberId) ??
    (editedMemberId ? memberSeeds.get(`${objID}:${editedMemberId}`) : undefined);

  // keepOpen: the contact is saved but the screen still has work to show (its
  // perimeters failed), so only the row is patched.
  const applySavedMember = (saved, { keepOpen = false } = {}) => {
    setCompanyInfo((previous) => ({
      ...previous,
      employees: (previous.employees ?? []).map((entry) =>
        entry.user?._id === saved._id
          ? {
              ...entry,
              role: saved.role,
              user: {
                ...entry.user,
                firstname: saved.firstname,
                lastname: saved.lastname,
                email: saved.email,
                phone: saved.phone,
                validation: saved.validation,
              },
            }
          : entry
      ),
    }));
    // The contact page that opened this edit reads the member again.
    notifyRecordChanged();
    if (keepOpen) return;
    if (memberOfTarget(target)) closeSlideOver();
    else setEditedMemberId(null);
  };

  return (
    <>
      {editedMember && memberOfTarget(target) ? (
        // Opened on a member by their contact page: shown at once from what it knew.
        <CompanyContactEditView
          companyId={objID}
          companyInfo={shownCompany}
          member={editedMember}
          perimeters={perimeters}
          scope={memberTargetOf(target)?.scope}
          onBack={closeSlideOver}
          onSaved={applySavedMember}
        />
      ) : isWaiting ? (
        <>
          <SlideOverHeader hide={closeSlideOver} hideShow={true}>
            <div>
              <div className="w-56 h-5 skeleton-bar"></div>
            </div>
          </SlideOverHeader>
          <SlideOverBody>
            <div className={'flex flex-col gap-2'}>
              <button
                type={'button'}
                className={'text-left border rounded p-3 mb-5'}
              >
                <div className={'flex justify-between'}>
                  <div className={'my-auto'}>
                    <div className="w-56 h-3 skeleton-bar"></div>
                  </div>
                  <div className={'my-auto'}>
                    <ChevronRight wh={24} color={'#111827'} />
                  </div>
                </div>
              </button>
              <div className={''}>
                <div
                  className={'border rounded p-3 flex justify-between gap-2'}
                >
                  <div className={'my-auto w-full'}>
                    <div className="w-56 h-3 skeleton-bar"></div>
                  </div>
                  <div className={'my-auto w-full'}>
                    <div className="w-56 h-6 skeleton-bar"></div>
                  </div>
                </div>
              </div>
              <div className={''}>
                <div
                  className={'border rounded p-3 flex justify-between gap-2'}
                >
                  <div className={'my-auto'}>
                    <div className="w-72 h-3 skeleton-bar"></div>
                  </div>
                </div>
              </div>
              <div className={'border rounded p-3 flex flex-col gap-1'}>
                <div className="w-56 h-3 skeleton-bar"></div>
                <div className="w-56 h-3 skeleton-bar"></div>
                <div className="w-36 h-3 skeleton-bar"></div>
              </div>
            </div>
          </SlideOverBody>
        </>
      ) : (
        <>
          {editedMember ? (
            <CompanyContactEditView
              companyId={objID}
              companyInfo={shownCompany}
              member={editedMember}
              perimeters={perimeters}
              onBack={() => setEditedMemberId(null)}
              onSaved={applySavedMember}
            />
          ) : !isShowCoeff ? (
            <>
              <SlideOverHeader
                hide={closeSlideOver}
                hideShow={true}
                extraActions={
                  <>
                    {/* Opened from a task's panel (TaskPanel), it leads back there. */}
                    {companyBackTaskId && (
                      <button
                        type="button"
                        onClick={() => openTask(companyBackTaskId)}
                        className="flex h-[30px] items-center rounded border px-3 text-sm font-normal text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        {language.getValue('tasks.backToTask')}
                      </button>
                    )}
                    {companyInfo?._id && pathname !== companyPath && (
                      <button
                        type="button"
                        onClick={openDetailedView}
                        className="flex h-[30px] items-center rounded border px-3 text-sm font-normal text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        {language.getValue('companyPanel.detailedView', 'Vue détaillée')}
                      </button>
                    )}
                  </>
                }
              >
                <div className={'flex flex-col'}>
                  {companyInfo?.name_commercial}
                  <div className={'text-sm text-gray-500'}>
                    <CopyToClipboard text={companyInfo?.id_bestt}>
                      {companyInfo?.id_bestt}
                    </CopyToClipboard>
                  </div>
                </div>
              </SlideOverHeader>
              <SlideOverBody>
                <div
                  className={
                    'flex flex-col gap-5 text-sm h-full overflow-y-auto'
                  }
                >
                  <div className={'flex items-center gap-2 w-full'}>
                    {isScrollable && (
                      <Button size="ICON" color="ICON" onClick={scrollLeft}>
                        <ChevronLeft wh={24} color="#111827" />
                      </Button>
                    )}
                    <div
                      ref={scrollContainerRef}
                      className={'flex gap-2 overflow-x-auto no-scrollbar'}
                    >
                      {shownTabs
                        .filter((tab) => tab !== 'invoices' || userData?.accessInvoice)
                        .map((tab) => (
                          <div
                            key={tab}
                            className={`cursor-pointer p-2 rounded-md whitespace-nowrap hover:bg-gray-100 hover:text-gray-900 ${
                              pageType === tab ? 'bg-gray-100' : 'text-gray-500'
                            }`}
                            onClick={() => setPageType(tab)}
                          >
                            {tabLabels[tab]}
                          </div>
                        ))}
                    </div>
                    {isScrollable && (
                      <Button size="ICON" color="ICON" onClick={scrollRight}>
                        <ChevronRight wh={24} color="#111827" />
                      </Button>
                    )}
                  </div>
                  {/* The tab's own scroll container: flex-1, not h-full (under the
                      tab bar a full height overflows the slide-over body), and
                      relative so an absolute child (an sr-only label) stays inside
                      it instead of stretching the body, which would scroll too. */}
                  <div className={'relative flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto'}>
                    {pageType === 'overview' && (
                      <SlideOverCompanyOverview
                        companyInfo={companyInfo}
                        setCompanyInfo={setCompanyInfo}
                        pennylaneCategoryList={pennylaneCategoryList}
                        bigAccount={bigAccount}
                        isActivityWaiting={
                          isCategoriesWaiting || isBigAccountWaiting
                        }
                        onReloadPennylaneCategories={loadPennylaneCategories}
                      />
                    )}
                    {pageType === 'members' && (
                      <SlideOverCompanyContactsTab
                        companyInfo={companyInfo}
                        setCompanyInfo={setCompanyInfo}
                        onEditMember={setEditedMemberId}
                        perimeters={perimeters}
                      />
                    )}
                    {pageType === 'remuneration_conditions' && (
                      <SlideOverCompanyRemunerationConditions
                        companyInfo={companyInfo}
                        setCompanyInfo={setCompanyInfo}
                        setIsShowCoeff={setIsShowCoeff}
                        setIsCoeffSend={setIsCoeffSend}
                        onSaved={() => fetchCompanyInfo(false)}
                      />
                    )}
                    {pageType === 'missions' && (
                      <SlideOverCompanyMissions
                        companyInfo={companyInfo}
                      />
                    )}
                    {/* Its tab shows with the invoice access only; a panel opened
                        on this tab without it shows nothing. Mounted with the
                        panel, hidden until its tab is chosen, so that its cards
                        have read their data by then; `contents` keeps them items
                        of this column, spaced like the other tabs' cards. */}
                    {userData?.accessInvoice && shownTabs.includes('invoices') && (
                      <div className={pageType === 'invoices' ? 'contents' : 'hidden'}>
                        <SlideOverCompanyInvoicesTab
                          companyInfo={companyInfo}
                          userData={userData}
                          isShown={pageType === 'invoices'}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </SlideOverBody>
            </>
          ) : (
            <>
              {/* The header of the task form (TaskFormPanel), the cross
                  included, and the grid's last change under its description. */}
              <SlideOverHeader
                hide={closeSlideOver}
                closeLabel={'Fermer'}
                description={
                  <>
                    Consulter et gérer le coefficient de facturation et les
                    conditions de paiement de l’entreprise utilisatrice.
                    {coefficientsLastUpdate ? (
                      <span className="mt-1 flex items-center gap-1.5">
                        <RotateFadingClock wh={16} color={'#6B7280'} />
                        <span>{coefficientsLastUpdate}</span>
                      </span>
                    ) : null}
                  </>
                }
              >
                Paramètres de paiement
              </SlideOverHeader>
              <SlideOverBody>
                <div className={'flex flex-col gap-4 h-full'}>
                  {coeffBanner ? (
                    <Banner type="alert">{coeffBanner}</Banner>
                  ) : !isCoeffSend || isCoeffOnDefaults ? (
                    <Banner type="warning">
                      {defaultCoefficientsSector
                        ? `Coefficients par défaut du secteur ${defaultCoefficientsSector}. Modifiez-les pour les adapter à cette entreprise.`
                        : 'Coefficients par défaut. Modifiez-les pour les adapter à cette entreprise.'}
                    </Banner>
                  ) : null}
                  {attestationLabel ? (
                    <div className={'text-gray-500 text-sm'}>
                      {attestationLabel}
                    </div>
                  ) : null}
                  {/* Bounded so the table's own wrapper is the scroller: its
                      `h-full` resolves to nothing in an unbounded parent, the
                      panel body scrolls instead, and the sticky header rides
                      away with it. */}
                  <div className={'flex-1 min-h-0'}>
                    <Table
                      champsBody={coeffRows}
                      setChampsBody={markCoeffTouched}
                      errors={coeffErrors}
                      champsHeader={titlesCoeff}
                      type={'coeff'}
                    />
                  </div>
                </div>
              </SlideOverBody>
              <SlideOverFooter>
                <button
                  type={'button'}
                  className={'underline text-sm mr-auto'}
                  onClick={restoreDefaults}
                >
                  {defaultCoefficientsSector
                    ? `Valeurs par défaut du secteur ${defaultCoefficientsSector}`
                    : 'Valeurs par défaut'}
                </button>
                <Button
                  size={'SM'}
                  color={'SECONDARY'}
                  onClick={discardCoeffDraft}
                >
                  Annuler
                </Button>
                <Button
                  size={'SM'}
                  color={canSubmitCoeff ? 'PRIMARY' : 'DISABLED'}
                  onClick={submitCoeff}
                >
                  Ajouter aux modifications
                </Button>
              </SlideOverFooter>
            </>
          )}
        </>
      )}
    </>
  );
};

export default SlideOverCompany;
